/**
 * Data layer for the COMPANY panel's MoneyMind section.
 *
 * MoneyMind is a reflection exercise, not a budgeting tool. A person logs the
 * things they spend money on and sorts each one into "Can't live without it",
 * "Maybe could go" or "Let it go". That sorting is the feature, and it is the
 * one field here that carries meaning without carrying anybody's finances.
 *
 * NO FIGURE ON THIS PAGE IS A SUM OF MONEY. Every entry has a price and a
 * currency; neither is ever fetched, totalled or shown. Three reasons, any one
 * sufficient: it is the person's own household spending and not an employer
 * metric at any level of aggregation; the section has a department filter, so
 * an "average" over four people in one team is four people's finances; and
 * entries mix seven currencies freely, so a total would be adding pounds to
 * rupees. The companion income-and-savings collection is not touched by any
 * endpoint behind this section.
 *
 * Backed by the company/v1 money-mind-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter can narrow the
 * whole page.
 *
 * The adapter is exported so a super-admin MoneyMind hub, served by the same
 * builders, can reuse the field mapping rather than restating it.
 */

import {
  getCompanyMoneyMindDashboard,
  getCompanyMoneyMindUsers,
  getCompanyMoneyMindUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Entry block shared by every MoneyMind dashboard. */
export const adaptMoneyMindEntries = (data) => ({
  // Reach and volume
  entries: data.entries ?? 0,
  users: data.users ?? 0,
  avgEntriesPerUser: data.avgEntriesPerUser ?? 0,
  firstEntryDate: data.firstEntryDate || null,
  lastEntryDate: data.lastEntryDate || null,

  // Habit rather than one-off: thirty entries on one afternoon is doing the
  // exercise once, and only distinct active days can tell the two apart.
  avgActiveDays: data.avgActiveDays ?? 0,
  returningUsers: data.returningUsers ?? 0,
  returnRate: data.returnRate ?? 0,

  // Depth — that a note was written, never what it says
  entriesWithNote: data.entriesWithNote ?? 0,
  noteRate: data.noteRate ?? 0,

  // The exercise itself: keep / unsure / let go
  tagMix: data.tagMix || [],

  // A count of entries per currency. Not a total in any currency.
  currencyMix: data.currencyMix || [],

  volumeSplit: data.volumeSplit || [],
  logLagSplit: data.logLagSplit || [],
  weekdayEntries: data.weekdayEntries || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  entriesTrend: (data.dailyTrend || []).map((day) => day.entries || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

export const fetchCompanyMoneyMindDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyMoneyMindDashboard(department));
    return {
      ...adaptMoneyMindEntries(data),
      // Company-only: the size of the population, so the page can say
      // "22 of 240 employees have used MoneyMind".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyMoneyMindDashboard error:', error);
    return null;
  }
};

// Matches the MoneyMindUsersTable fetcher contract.
export const fetchCompanyMoneyMindUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyMoneyMindUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyMoneyMindUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's MoneyMind profile ({ user, tagMix, currencyMix,
 * monthlyActivity }). Counts and dates only — no amount, nothing about what was
 * bought, and none of the reflective notes. The backend 404s any user outside
 * this company, so no scoping is needed here.
 */
export const fetchCompanyMoneyMindUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyMoneyMindUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyMoneyMindUserDetail error:', error);
    return null;
  }
};
